import ReactDOM from "react-dom";
import { cloneElement, useMemo, useState } from "react";
import {
  offset,
  autoUpdate,
  useFloating,
  useInteractions,
  useDismiss,
  useClick,
  Strategy,
  Placement,
  useHover,
} from "@floating-ui/react-dom-interactions";
import { mergeRefs } from "react-merge-refs";
import styled, { css } from "styled-components";
import { ReactComponent as CloseIcon } from "../assets/images/circle-close.svg";
import { Text } from "./Text";
import { ROOT_ID } from "../utils/constants";

type TooltipSize = "sm" | "md";
interface TooltipProps {
  label: string;
  children: JSX.Element;
  size?: TooltipSize;
  placement?: Placement;
  event?: "click" | "hover";
  hideCloseButton?: boolean;
}

export function Tooltip({
  children,
  label,
  size = "md",
  event = "click",
  placement = "right",
  hideCloseButton = false,
}: TooltipProps) {
  const [open, setOpen] = useState(false);

  const { x, y, reference, floating, strategy, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [offset(12)],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: event === "click" }),
    useHover(context, { enabled: event === "hover" }),
    useDismiss(context),
  ]);

  // Preserve the consumer's ref
  const ref = useMemo(
    () => mergeRefs([reference, (children as any).ref]),
    [reference, children],
  );

  return (
    <>
      {cloneElement(children, getReferenceProps({ ref, ...children.props }))}
      {open && (
        <ToolTipPortal
          label={label}
          floating={floating}
          x={x}
          y={y}
          strategy={strategy}
          getFloatingProps={getFloatingProps}
          size={size}
          placement={placement}
          hideCloseButton={hideCloseButton}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

type ToolTipPortalProps = {
  label: string;
  strategy: Strategy;
  floating: (node: HTMLElement | null) => void;
  x: number | null;
  y: number | null;
  getFloatingProps: (
    userProps?: React.HTMLProps<HTMLElement> | undefined,
  ) => Record<string, unknown>;
  size: TooltipSize;
  placement: Placement;
  hideCloseButton?: boolean;
  onClose: () => void;
};

function ToolTipPortal({
  label,
  strategy,
  floating,
  x,
  y,
  getFloatingProps,
  size,
  placement,
  hideCloseButton,
  onClose,
}: ToolTipPortalProps) {
  const defaultNode = document.getElementById(`${ROOT_ID}_tooltip`);

  if (!defaultNode) {
    throw new Error(`${ROOT_ID}_tooltip element not found`);
  }

  return ReactDOM.createPortal(
    <Container
      ref={floating}
      size={size}
      placement={placement}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
      }}
      {...getFloatingProps()}
    >
      {!hideCloseButton && (
        <CloseButton onClick={() => onClose()}>
          <CloseIcon />
        </CloseButton>
      )}
      <Description size={size}>{label}</Description>
    </Container>,
    defaultNode,
  );
}

const Container = styled.div<{ size: TooltipSize; placement: Placement }>`
  z-index: 15;
  max-width: 400px;
  margin-right: ${({ theme }) => theme.getValue(10)}px;
  padding: ${({ theme, size }) =>
    size === "sm"
      ? `${theme.getValue(4)}px ${theme.getValue(10)}px`
      : `${theme.getValue(14)}px`};
  border-radius: ${({ size }) => (size === "sm" ? 8 : 12)}px;
  background: ${({ theme }) => theme.colors.grey1};

  &:before {
    position: absolute;
    content: "";
    border-style: solid;
    border-width: ${({ size }) => (size === "sm" ? 6 : 9)}px;

    ${({ placement, size }) => {
      switch (placement) {
        case "right":
          return css`
            top: 50%;
            left: ${size === "sm" ? -18 : -18}px;
            transform: translateY(-50%);
            border-color: ${({ theme }) =>
              `transparent ${theme.colors.grey1} transparent transparent`};
          `;
        case "top":
          return css`
            bottom: ${size === "sm" ? -12 : -18}px;
            left: 50%;
            transform: translateX(-50%);
            border-color: ${({ theme }) =>
              `${theme.colors.grey1} transparent transparent transparent`};
          `;
        default:
          return "";
      }
    }}
  }
`;

const CloseButton = styled.button`
  margin: 0;
  padding: 0;
  cursor: pointer;
  border: 0;
  background: none;
  appearance: none;

  svg {
    width: ${({ theme }) => theme.getValue(23)}px;
    height: ${({ theme }) => theme.getValue(23)}px;
  }
`;

const Description = styled(Text)<{ size: TooltipSize }>`
  &&& {
    font-size: ${({ theme, size }) =>
      size === "sm" ? theme.getFontSize(14) : theme.getFontSize(16)}px;
    color: ${({ theme, textColor }) => textColor?.hex || theme.colors.white};
    line-height: 1.25;
    margin: ${({ theme }) => `${theme.getValue(4)}px 0 0`};
  }
`;
